import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { triggerBlobDownload } from '../../../shared/download.util';

export interface ContractDocumentsData {
  contractId: number;
  /** The contract as the table names it: its number, else its title, else its id. */
  contractLabel: string;
  /** Who the contract is with, said under the title. */
  ownerName: string | null;
}

/** One stored file. Mirrors ContractDocumentDto. */
export interface ContractDocument {
  id: number;
  docType: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  isGenerated: boolean;
  uploadedAt: string;
}

/**
 * What the server stores, checked here as well so a file it would refuse is
 * refused before it is sent. The server stays the authority - its list is
 * ContractLogic's UploadedDocumentContentTypes, its limit
 * ContractDocumentLimits - and says why when it refuses anyway.
 */
export const CONTRACT_DOCUMENT_EXTENSIONS: readonly string[] =
  ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
export const CONTRACT_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** What each stored kind of file is, in words. */
const DOCUMENT_KINDS: Readonly<Record<string, string>> = {
  Draft: 'Generated PDF',
  Signed: 'Signed copy',
  Attachment: 'Attachment',
  Certificate: 'Signing certificate',
};

/**
 * The files stored for one contract: every one listed, a PDF or a picture
 * opened in the browser, anything saved, and the signed copy added.
 *
 * Nothing here stores bytes of its own. The files live in contract_document
 * on the server; this is the way to them from the contract table, which had
 * none - the only upload was on the customer page and nothing listed them.
 */
@Component({
  selector: 'app-contract-documents-dialog',
  templateUrl: './contract-documents-dialog.component.html',
  styleUrls: ['./contract-documents-dialog.component.css'],
})
export class ContractDocumentsDialogComponent implements OnInit, OnDestroy {
  documents: ContractDocument[] = [];
  loading = false;
  uploading = false;
  error: string | null = null;
  readonly accept = CONTRACT_DOCUMENT_EXTENSIONS.join(',');

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) readonly data: ContractDocumentsData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /** Newest first, which is the order the server returns them in. */
  load(): void {
    this.loading = true;
    this.http.get<ContractDocument[]>(`/api/contracts/${this.data.contractId}/documents`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: documents => {
          this.documents = documents;
          this.loading = false;
        },
        error: failure => {
          this.loading = false;
          this.error = serverSays(failure, 'Could not list the files stored for this contract.');
        },
      });
  }

  kind(stored: ContractDocument): string {
    return DOCUMENT_KINDS[stored.docType] ?? stored.docType;
  }

  /** A browser shows a PDF or a picture itself; a Word file it can only save. */
  canOpen(stored: ContractDocument): boolean {
    return stored.contentType === 'application/pdf' || stored.contentType.startsWith('image/');
  }

  open(stored: ContractDocument): void {
    this.show(this.read(stored), 'Could not open that file.');
  }

  /** The contract as the system renders it from its current terms. */
  openContractPdf(): void {
    this.show(
      this.http.get(`/api/contracts/${this.data.contractId}/pdf`, { responseType: 'blob' })
        .pipe(takeUntil(this.destroyed$)),
      'Could not open the contract PDF.');
  }

  download(stored: ContractDocument): void {
    this.read(stored).subscribe({
      next: blob => triggerBlobDownload(blob, stored.fileName),
      error: failure => {
        this.error = serverSays(failure, 'Could not download that file.');
      },
    });
  }

  /** Why a file would be refused, or null when it would be stored. */
  refusal(file: File): string | null {
    const dot = file.name.lastIndexOf('.');
    const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
    if (!CONTRACT_DOCUMENT_EXTENSIONS.includes(extension)) {
      return 'A contract file has to be a PDF, a Word document or an image.';
    }
    if (file.size === 0) return 'That file is empty.';
    if (file.size > CONTRACT_DOCUMENT_MAX_BYTES) return 'A contract file can be at most 10 MB.';
    return null;
  }

  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared, so choosing the same file again still counts as a choice.
    input.value = '';
    if (file) this.upload(file);
  }

  /**
   * Stores the file as the contract's signed copy - the one upload the
   * server takes. Storing does not change the contract's status.
   */
  upload(file: File): void {
    if (this.uploading) return;
    const refusal = this.refusal(file);
    if (refusal) {
      this.error = refusal;
      return;
    }
    const form = new FormData();
    form.append('file', file, file.name);
    this.uploading = true;
    this.error = null;
    this.http.post(`/api/contracts/${this.data.contractId}/document`, form)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.uploading = false;
          this.load();
        },
        error: failure => {
          this.uploading = false;
          this.error = serverSays(failure, 'Could not store that file.');
        },
      });
  }

  size(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private read(stored: ContractDocument): Observable<Blob> {
    return this.http
      .get(`/api/contracts/${this.data.contractId}/documents/${stored.id}`, { responseType: 'blob' })
      .pipe(takeUntil(this.destroyed$));
  }

  /**
   * Opens the window first, while the click still counts as the person's: a
   * window opened once the file has arrived is a popup, and the browser
   * blocks it. It is pointed at the file when the file is in hand.
   */
  private show(file: Observable<Blob>, failure: string): void {
    const view = window.open('', '_blank');
    file.subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        if (view) view.location.replace(url);
        else window.open(url, '_blank');
      },
      error: problem => {
        view?.close();
        this.error = serverSays(problem, failure);
      },
    });
  }
}

/** The server's own sentence when it gave one; ours otherwise. */
function serverSays(failure: unknown, fallback: string): string {
  const said = (failure as { error?: { error?: unknown } } | null)?.error?.error;
  return typeof said === 'string' && said ? said : fallback;
}
